/* Confirmation for two squares changing places.
 *
 * Not the move sheet with a second row. The move sheet asks about the timer,
 * because a client moving to a different chair mid-service could mean either
 * thing; this one has no question of that kind in it. Two services trading
 * places are both still the services they were — each client sat down when they
 * sat down — so both clocks simply carry on, and there is nothing to ask.
 *
 * Two swaps arrive here, and the difference is what the desk is being shown:
 *
 *   book    the crossed-over pair. The swap is a correction, and the reason to
 *           press the button is that the booking says so — so the sheet shows
 *           the booking, both sides, times and technicians. If the desk cannot
 *           see the swap matching the book they should not take the board's
 *           word for it.
 *   board   a service dragged onto another service. Nothing is wrong with the
 *           record; the room changed and the board is being told. The reason to
 *           press the button is that the desk watched it happen, so the sheet's
 *           job is to show it read the drop right — who is going where, and how
 *           long each client has already been sitting.
 *
 * Either way the check that raised the sheet is asked again on every render. A
 * sheet can sit open across a webhook or another station's drag, and one whose
 * swap has stopped being possible says so and takes its own button away rather
 * than failing on press.
 */
import * as Vue from 'vue';
import { store } from '../store.js';

export const SwapConfirm = {
  name: 'SwapConfirm',
  setup: function () {
    var d = store.swapConfirmDetail;

    var sides = Vue.computed(function () {
      return d.value ? [d.value.a, d.value.b] : [];
    });

    // Whole sentences rather than text stitched around {{ }}: the compiler
    // eats the whitespace either side of an interpolation and the seams show.
    var headline = Vue.computed(function () {
      if (!d.value) return '';
      var a = d.value.a, b = d.value.b;
      if (d.value.kind === 'book') return a.from + ' and ' + b.from + ' have each other’s client.';
      return a.from + ' and ' + b.from + ' take over each other’s service.';
    });

    var reconcile = Vue.computed(function () {
      if (!d.value) return '';
      var a = d.value.a, b = d.value.b;
      if (d.value.kind === 'book') {
        return 'The book has ' + (a.clientName || a.serviceName) + ' with ' + a.to +
          ' and ' + (b.clientName || b.serviceName) + ' with ' + b.to +
          ', so swapping the two squares leaves nothing flagged.';
      }
      return a.to + ' picks up ' + (a.clientName || a.serviceName) + ' and ' +
        b.to + ' picks up ' + (b.clientName || b.serviceName) +
        '. Neither service is being started again — both are already under way.';
    });

    var title = Vue.computed(function () {
      if (!d.value) return '';
      return d.value.kind === 'book' ? 'Swap these two?' : 'Swap these two services?';
    });

    function bookedLine(s) {
      var when = s.bookedAt ? store.formatClock(s.bookedAt) : null;
      if (!s.bookedTech) return when ? 'Booked ' + when : '';
      return when
        ? 'Booked ' + when + ' with ' + s.bookedTech
        : 'Booked with ' + s.bookedTech;
    }

    return {
      d: d,
      sides: sides,
      title: title,
      headline: headline,
      reconcile: reconcile,
      bookedLine: bookedLine,
      close: store.closeSwapConfirm,
      confirm: store.confirmSwap
    };
  },
  template: [
    '<div v-if="d" class="modal-scrim" @click.self="close">',
    '  <div class="sheet" role="dialog" aria-modal="true">',
    '    <div class="sheet__head" :style="{ \'--svc\': d.a.color }">',
    '      <span class="sheet__letter sheet__letter--swap" aria-hidden="true">&#8646;</span>',
    '      <div>',
    '        <h3>{{ title }}</h3>',
    '        <p>{{ headline }}</p>',
    '      </div>',
    '    </div>',
    '',
    '    <div class="assign__section">',
    '      <ul class="swap-pair">',
    '        <li v-for="s in sides" :key="s.id" class="swap-side">',
    '          <span class="swap-side__letter" :style="{ background: s.color }">{{ s.letter }}</span>',
    '          <span class="swap-side__body">',
    '            <span class="swap-side__who">{{ s.clientName || s.serviceName }}<client-note :client-id="s.clientId" /><note-warn :client-id="s.clientId" :staff-id="s.toId" /></span>',
    '            <span class="swap-side__what">{{ s.serviceName }}</span>',
    '            <span v-if="bookedLine(s)" class="swap-side__book">{{ bookedLine(s) }}</span>',
    /* How long this client has been sitting. On the hand-made swap it is the
       thing that identifies the square — two services on the board at once are
       told apart by their clocks long before anybody reads the names. */
    '            <span v-if="d.kind === \'board\' && s.running" class="swap-side__ran">',
    '              Running for {{ s.elapsed }}</span>',
    '          </span>',
    '          <span class="swap-side__move">{{ s.from }}',
    '            <span class="swap-side__arrow" aria-hidden="true">&rarr;</span>',
    '            <strong>{{ s.to }}</strong></span>',
    '        </li>',
    '      </ul>',
    '    </div>',
    '',
    /* The book has moved under the sheet. Said plainly, with the button gone,
       rather than left to fail on press. */
    '    <div v-if="d.blocked" class="assign__section">',
    '      <p class="warn-body">{{ d.blocked }}</p>',
    '      <p class="sheet__hint">Nothing has been changed. Close this and read the list again.</p>',
    '    </div>',
    '    <div v-else class="assign__section">',
    '      <p class="warn-body">{{ reconcile }}</p>',
    /* What the desk is about to disagree with the book about. A warning and not
       a refusal: they are standing in the room and the booking system is not,
       so the only thing owed here is that the flag it leaves behind is not a
       surprise when it appears. */
    '      <ul v-if="d.warn" class="sheet__conflicts">',
    '        <li><span class="sheet__conflict-mark" aria-hidden="true">&#9888;</span>',
    '          <span>{{ d.warn }}</span></li>',
    '      </ul>',
    '      <p v-if="d.kind === \'book\'" class="sheet__hint">Both clocks keep running. Each square',
    '        keeps its own start &mdash; the name over the column was wrong, not the time under',
    '        the square &mdash; and its expected finish is taken from the booking again on the',
    '        way past.</p>',
    '      <p v-else class="sheet__hint">Both clocks keep running. Each client keeps the time they',
    '        have already been sitting &mdash; they changed chairs, they did not start again',
    '        &mdash; and each square keeps its own expected finish.</p>',
    '    </div>',
    '',
    '    <div class="sheet__actions">',
    '      <button v-if="!d.blocked" type="button" class="btn btn--lg"',
    '        :class="d.warn ? \'btn--danger\' : \'btn--primary\'" @click="confirm">',
    '        {{ d.warn ? "Swap them anyway" : "Swap them" }}</button>',
    '      <button type="button" :class="d.blocked ? \'btn btn--primary btn--lg\' : \'btn btn--quiet\'"',
    '        @click="close">{{ d.blocked ? "Close" : "Cancel" }}</button>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
